// src/pages/Installation.jsx
import React from "react";
import { motion } from "framer-motion";
import Tools from "../images/Installation.jpg";

export default function InstallationPage() {
  return (
    <>
      {/* 🔥 JSON-LD BREADCRUMB SCHEMA */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://www.ledotprime.com/",
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Installation",
              "item": "https://www.ledotprime.com/installation",
            },
          ],
        })}
      </script>

      <div className="w-full flex flex-col items-center bg-white text-gray-800 pt-20">

        {/* HERO SECTION */}
        <section className="w-full max-w-6xl py-20 px-6 md:px-0 flex flex-col md:flex-row items-center gap-12">
          
          {/* Text */}
          <div className="flex-1 space-y-8 text-center md:text-left">
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight tracking-tight">
              Professional  
              <span className="text-orange-600"> GPS Installation </span>
              Across Ghana
            </h1>

            <p className="text-lg text-gray-600 max-w-xl mx-auto md:mx-0 leading-relaxed">
              Ledot Prime installation experts are positioned all across Ghana —
              ensuring fast, seamless, and highly professional installations,
              no matter your location.  
              We combine skill, experience, and precision to give your vehicle
              the perfect GPS setup from the start.
            </p>
          </div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex-1 w-full"
          >
            <img
              src={Tools}
              alt="Installation Process"
              className="rounded-3xl shadow-xl object-cover w-full h-[280px] md:h-[430px] transform transition-transform duration-500 hover:scale-105"
            />
          </motion.div>
        </section>

        {/* WHY CHOOSE OUR INSTALLATION */}
        <section className="w-full bg-gray-50 py-20 px-6 md:px-0">
          <div className="max-w-6xl mx-auto grid sm:grid-cols-2 md:grid-cols-3 gap-8">

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow p-8 space-y-4 hover:shadow-xl transition-all duration-300"
            >
              <h3 className="text-2xl font-bold">📍 Nationwide Coverage</h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                Whether you're in Accra, Kumasi, Takoradi, Tamale, Ho, Cape Coast,
                or any part of Ghana — our experts will come to you.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow p-8 space-y-4 hover:shadow-xl transition-all duration-300"
            >
              <h3 className="text-2xl font-bold">🛠️ Skilled & Experienced</h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                Every installer is trained, vetted, and equipped to deliver
                clean, secure, and professional GPS setups.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow p-8 space-y-4 hover:shadow-xl transition-all duration-300"
            >
              <h3 className="text-2xl font-bold">⚡ Fast Mobile Service</h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                No need to come to us — our team arrives at your home, office,
                shop, or any preferred location for instant installation.
              </p>
            </motion.div>

          </div>
        </section>

        {/* INSTALLATION PROCESS */}
        <section className="w-full max-w-6xl py-20 px-6 md:px-0">
          <h2 className="text-4xl md:text-5xl font-extrabold text-center mb-14">
            How Installation Works
          </h2>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8">

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center bg-white shadow p-8 rounded-2xl hover:scale-105 transition-all duration-300"
            >
              <div className="text-4xl mb-4">📦</div>
              <h4 className="font-bold text-xl mb-2">1. Request Service</h4>
              <p className="text-gray-600 text-lg">
                Book installation through our website or WhatsApp.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center bg-white shadow p-8 rounded-2xl hover:scale-105 transition-all duration-300"
            >
              <div className="text-4xl mb-4">🚗</div>
              <h4 className="font-bold text-xl mb-2">2. Expert Arrives</h4>
              <p className="text-gray-600 text-lg">
                A trained technician is sent to your exact location.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center bg-white shadow p-8 rounded-2xl hover:scale-105 transition-all duration-300"
            >
              <div className="text-4xl mb-4">🔧</div>
              <h4 className="font-bold text-xl mb-2">3. Professional Setup</h4>
              <p className="text-gray-600 text-lg">
                Your GPS is installed neatly and securely within minutes.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center bg-white shadow p-8 rounded-2xl hover:scale-105 transition-all duration-300"
            >
              <div className="text-4xl mb-4">📱</div>
              <h4 className="font-bold text-xl mb-2">4. Activation & Training</h4>
              <p className="text-gray-600 text-lg">
                We activate your device and teach you how to use it.
              </p>
            </motion.div>

          </div>
        </section>

        {/* CTA SECTION */}
        <section className="w-full bg-black text-white py-20 px-6 text-center mt-12 rounded-t-3xl">
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4">
            Ready for a Smooth Installation?
          </h2>

          <p className="text-lg md:text-xl mb-8 text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Our experts are available 24/7 nationwide.  
            Book now and get your GPS installed today.
          </p>

          <a
            href={`https://wa.me/233508499531?text=${encodeURIComponent(
              "Hello, I want to book a GPS installation service."
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-black font-semibold px-10 py-4 rounded-xl text-lg md:text-xl inline-block transition-transform duration-300 hover:scale-110 hover:bg-gray-100"
          >
            Book Installation
          </a>
        </section>

      </div>
    </>
  );
}
